import React, { useEffect, useState, useRef } from "react";
import { ym } from "../lib/dates";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, PencilSimple, Trash, Printer, FileArrowUp, Download } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError, formatMonth } from "../lib/api";
import { useAuth } from "../lib/auth";
import { generatePDF } from "../lib/pdf";

const SCALE_MAX = 5;
const TABS = [
  { key: "list", label: "Daftar" },
  { key: "monthly", label: "Input Per Bulan" },
  { key: "rekap", label: "Rekap Per Lembaga" },
];

// Label pendek untuk header PDF/tabel agar lebih rapi
const SHORT_LABEL = {
  presensi: "Presensi",
  kedisiplinan: "Disiplin",
  kerapian: "Kerapian",
  administrasi: "Admin.",
  adab_akhlak: "Adab",
  inisiatif_kreativitas: "Inisiatif",
  pendampingan_siswa: "Pendmp.",
  kerjasama_komunikasi: "Kerjasama",
  ibadah: "Ibadah",
  kepemimpinan: "Leadrshp",
};
const shortLbl = (ind) => SHORT_LABEL[ind.key] || ind.label.split(" ")[0];

export default function Rapor() {
  const { user } = useAuth();
  const isKaryawan = user?.role === "karyawan";
  const canManage = ["superadmin", "admin", "hr"].includes(user?.role);
  const [tab, setTab] = useState("list");
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [filterPeriod, setFilterPeriod] = useState(ym());
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [monthlyForm, setMonthlyForm] = useState({ period: filterPeriod, lembaga_id: "all", rows: {} });
  const [rekap, setRekap] = useState(null);
  const fileInput = useRef();

  const load = async () => {
    const [r, e, ind, l] = await Promise.all([
      isKaryawan ? api.get("/rapor") : api.get(`/rapor${filterPeriod !== "all" ? `?period=${filterPeriod}` : ""}`),
      api.get("/employees").catch(() => ({ data: [] })),
      api.get("/rapor-indicators"),
      api.get("/lembaga").catch(() => ({ data: [] })),
    ]);
    setItems(r.data); setEmployees(e.data); setIndicators(ind.data); setLembagas(l.data);
  };
  useEffect(() => { load(); }, [filterPeriod, isKaryawan]);

  const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));
  const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l.name]));
  const emptyScores = () => Object.fromEntries(indicators.map((i) => [i.key, 0]));

  const openNew = () => {
    setEditing(null);
    setForm({
      employee_id: employees[0]?.employee_id || "",
      period: filterPeriod === "all" ? ym() : filterPeriod,
      scores: emptyScores(),
      terlambat_count: 0,
      tidak_masuk_count: 0,
      catatan: "",
    });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      employee_id: r.employee_id,
      period: r.period,
      scores: { ...emptyScores(), ...(r.scores || {}) },
      terlambat_count: r.terlambat_count || 0,
      tidak_masuk_count: r.tidak_masuk_count || 0,
      catatan: r.catatan || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const scores = {};
      indicators.forEach((i) => { scores[i.key] = Math.max(0, Math.min(SCALE_MAX, Number(form.scores?.[i.key] || 0))); });
      const payload = {
        employee_id: form.employee_id, period: form.period, scores,
        terlambat_count: Number(form.terlambat_count || 0),
        tidak_masuk_count: Number(form.tidak_masuk_count || 0),
        catatan: form.catatan || null,
      };
      if (editing) await api.put(`/rapor/${editing.id}`, payload);
      else await api.post("/rapor", payload);
      toast.success("Rapor tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Hapus rapor?")) return; try { await api.delete(`/rapor/${id}`); load(); toast.success("Terhapus"); } catch { toast.error("Gagal"); } };

  // === Excel ===
  const downloadTemplate = () => {
    const headers = ["employee_id", "period", "terlambat_count", "tidak_masuk_count", ...indicators.map((i) => i.key), "catatan"];
    const example = ["EMP-100001", "2026-02", 2, 1, ...indicators.map(() => 4), "Kinerja baik"];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    XLSX.utils.book_append_sheet(wb, ws, "Rapor");
    XLSX.writeFile(wb, "template-rapor-guru.xlsx");
  };

  const importFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      const rows = data.map((d) => {
        const scores = {};
        indicators.forEach((ind) => { if (d[ind.key] !== undefined) scores[ind.key] = Number(d[ind.key]); });
        return {
          employee_id: String(d.employee_id || ""), period: String(d.period || ""), scores,
          terlambat_count: Number(d.terlambat_count || 0),
          tidak_masuk_count: Number(d.tidak_masuk_count || 0),
          catatan: d.catatan ? String(d.catatan) : null,
        };
      }).filter((r) => r.employee_id && r.period);
      const { data: res } = await api.post("/rapor/bulk", rows);
      toast.success(`${res.inserted} rapor diimport`);
      if (res.errors?.length) alert(`${res.errors.length} gagal:\n` + res.errors.slice(0, 10).map((e) => `${e.employee_id}: ${e.error}`).join("\n"));
      load();
    } catch (e) { toast.error("Gagal import: " + (e.message || "")); }
  };

  const printOne = (r) => {
    const emp = empMap[r.employee_id] || {};
    const scoreRows = indicators.map((ind) => [ind.label, `${r.scores?.[ind.key] ?? "-"} / ${SCALE_MAX}`]);
    generatePDF({
      title: `Rapor Guru/Karyawan – ${formatMonth(r.period)}`,
      subtitle: emp.name || r.employee_id,
      headers: ["Aspek", "Nilai (Skala 0-5)"],
      rows: [
        ["Terlambat", String(r.terlambat_count || 0)],
        ["Tidak Masuk (Izin/Sakit)", String(r.tidak_masuk_count || 0)],
        ...scoreRows,
        ["Rata-rata", String(r.average)],
        ["Grade", r.grade],
      ],
      meta: {
        "Nama Guru/Karyawan": emp.name || "-", "Jabatan": emp.position || "-",
        "Lembaga": lembagaMap[emp.lembaga_id] || "-", "Bulan": formatMonth(r.period),
        "Catatan": r.catatan || "-",
      },
      filename: `rapor-${r.employee_id}-${r.period}.pdf`,
    });
  };

  // === Monthly bulk ===
  const loadMonthlyGrid = () => {
    const targetEmps = employees.filter((e) => monthlyForm.lembaga_id === "all" || e.lembaga_id === monthlyForm.lembaga_id);
    const rows = {};
    targetEmps.forEach((e) => {
      const existing = items.find((r) => r.employee_id === e.employee_id && r.period === monthlyForm.period);
      rows[e.employee_id] = existing ? {
        scores: { ...emptyScores(), ...(existing.scores || {}) },
        terlambat_count: existing.terlambat_count || 0, tidak_masuk_count: existing.tidak_masuk_count || 0,
        catatan: existing.catatan || "",
      } : { scores: emptyScores(), terlambat_count: 0, tidak_masuk_count: 0, catatan: "" };
    });
    setMonthlyForm({ ...monthlyForm, rows });
  };
  useEffect(() => { if (tab === "monthly" && employees.length && indicators.length) loadMonthlyGrid(); }, [tab, employees, indicators, monthlyForm.period, monthlyForm.lembaga_id]);

  const saveMonthly = async () => {
    const rows = Object.entries(monthlyForm.rows).map(([employee_id, r]) => ({
      employee_id, period: monthlyForm.period, scores: r.scores,
      terlambat_count: Number(r.terlambat_count || 0),
      tidak_masuk_count: Number(r.tidak_masuk_count || 0),
      catatan: r.catatan || null,
    }));
    if (!rows.length) return toast.error("Tidak ada baris untuk disimpan");
    try {
      const { data: res } = await api.post("/rapor/bulk", rows);
      toast.success(`${res.inserted} rapor tersimpan`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // === Rekap ===
  const loadRekap = async () => {
    try {
      const q = new URLSearchParams({ period: filterPeriod === "all" ? ym() : filterPeriod });
      if (filterLembaga !== "all") q.set("lembaga_id", filterLembaga);
      const { data } = await api.get(`/rapor/rekap?${q}`);
      setRekap(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (tab === "rekap") loadRekap(); }, [tab, filterPeriod, filterLembaga]);

  const printRekap = () => {
    if (!rekap) return;
    const lembagaLabel = filterLembaga === "all" ? "Seluruh Lembaga" : (lembagaMap[filterLembaga] || "-");
    generatePDF({
      title: `Rekap Rapor Guru ${formatMonth(rekap.period)}`,
      subtitle: lembagaLabel,
      headers: ["Guru", "Jabatan", "Lembaga", "Trlbt", "Tdk Msk", ...indicators.map(shortLbl), "Rata²", "Grade"],
      rows: rekap.rows.map((r) => [r.name || r.employee_id, r.position || "-", r.lembaga_name || "-", r.terlambat_count, r.tidak_masuk_count, ...indicators.map((i) => r.scores?.[i.key] ?? "-"), r.average, r.grade]),
      meta: { "Periode": formatMonth(rekap.period), "Lembaga": lembagaLabel, "Total": rekap.count },
      filename: `rekap-rapor-${rekap.period}-${filterLembaga}.pdf`,
    });
  };
  const exportRekapExcel = () => {
    if (!rekap) return;
    const headers = ["Guru", "Jabatan", "Lembaga", "Terlambat", "Tidak Masuk", ...indicators.map((i) => i.label), "Rata²", "Grade", "Catatan"];
    const rows = rekap.rows.map((r) => [r.name || r.employee_id, r.position || "", r.lembaga_name || "", r.terlambat_count, r.tidak_masuk_count, ...indicators.map((i) => r.scores?.[i.key] ?? 0), r.average, r.grade, r.catatan || ""]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, "Rekap Rapor");
    XLSX.writeFile(wb, `rekap-rapor-${rekap.period}-${filterLembaga}.xlsx`);
  };

  return (
    <div data-testid="rapor-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Human Resource</div>
          <h1 className="font-display text-3xl font-black text-foreground">Rapor {isKaryawan ? "Saya" : "Guru & Karyawan"}</h1>
          <p className="text-muted-foreground text-sm mt-1">Penilaian bulanan skala 0–5, catat keterlambatan & ketidakhadiran.</p>
        </div>
      </div>

      {!isKaryawan && (
        <div className="mb-4 flex gap-1 border-b border-border" data-testid="rapor-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`rapor-tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "text-[#0B8D95] border-[#0B8D95]" : "text-muted-foreground border-transparent hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {(isKaryawan || tab === "list") && (
        <>
          <div className="bg-card border border-border rounded-xl p-3 mb-3 flex flex-wrap gap-3 items-end">
            <div><Label className="text-xs">Periode</Label><Input type="month" value={filterPeriod === "all" ? "" : filterPeriod} onChange={(e) => setFilterPeriod(e.target.value || "all")} className="w-40" data-testid="rapor-period-filter" /></div>
            {canManage && (
              <div className="flex gap-2 flex-wrap ml-auto">
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="rapor-template-button"><Download size={14} className="mr-1.5" />Template Excel</Button>
                <input ref={fileInput} type="file" accept=".xlsx,.xls" hidden onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} data-testid="rapor-import-file" />
                <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()} data-testid="rapor-import-button"><FileArrowUp size={14} className="mr-1.5" />Import Excel</Button>
                <Button size="sm" onClick={openNew} data-testid="rapor-add-button"><Plus size={14} className="mr-1.5" />Buat Rapor</Button>
              </div>
            )}
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  {!isKaryawan && <th className="px-3 py-2.5">Guru</th>}
                  <th className="px-3 py-2.5">Bulan</th>
                  <th className="px-3 py-2.5 text-center">Terlambat</th>
                  <th className="px-3 py-2.5 text-center">Tdk Masuk</th>
                  {indicators.map((i) => <th key={i.key} className="px-2 py-2.5 text-center whitespace-nowrap">{shortLbl(i)}</th>)}
                  <th className="px-3 py-2.5 text-center">Rata²</th>
                  <th className="px-3 py-2.5 text-center">Grade</th>
                  <th className="px-3 py-2.5 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                    {!isKaryawan && <td className="px-3 py-2.5 font-semibold">{empMap[r.employee_id]?.name || r.employee_id}</td>}
                    <td className="px-3 py-2.5 font-mono text-xs">{formatMonth(r.period)}</td>
                    <td className="px-3 py-2.5 text-center font-mono">{r.terlambat_count || 0}</td>
                    <td className="px-3 py-2.5 text-center font-mono">{r.tidak_masuk_count || 0}</td>
                    {indicators.map((i) => <td key={i.key} className="px-2 py-2.5 text-center font-mono">{r.scores?.[i.key] ?? "-"}</td>)}
                    <td className="px-3 py-2.5 text-center font-mono font-bold">{r.average}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${r.grade === "A" ? "bg-emerald-100 text-emerald-700" : r.grade === "B" ? "bg-blue-100 text-blue-700" : r.grade === "C" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{r.grade}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => printOne(r)} className="p-1.5 hover:bg-muted rounded" data-testid={`rapor-print-${r.id}`}><Printer size={14} /></button>
                      {canManage && <>
                        <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-muted rounded" data-testid={`rapor-edit-${r.id}`}><PencilSimple size={14} /></button>
                        <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" data-testid={`rapor-delete-${r.id}`}><Trash size={14} /></button>
                      </>}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={5 + indicators.length + (isKaryawan ? 0 : 1)} className="px-5 py-10 text-center text-muted-foreground">Belum ada rapor</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!isKaryawan && tab === "monthly" && canManage && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-3 border-b border-border flex flex-wrap gap-3 items-end">
            <div><Label className="text-xs">Bulan</Label><Input type="month" value={monthlyForm.period} onChange={(e) => setMonthlyForm({ ...monthlyForm, period: e.target.value })} className="w-40" data-testid="rapor-monthly-period" /></div>
            <div><Label className="text-xs">Lembaga</Label>
              <Select value={monthlyForm.lembaga_id} onValueChange={(v) => setMonthlyForm({ ...monthlyForm, lembaga_id: v })}>
                <SelectTrigger className="w-56" data-testid="rapor-monthly-lembaga"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button className="ml-auto" onClick={saveMonthly} data-testid="rapor-monthly-save"><Plus size={14} className="mr-1.5" />Simpan Semua</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted text-left uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold sticky left-0 bg-muted">Guru</th>
                  <th className="px-2 py-2 font-semibold text-center">Terlambat</th>
                  <th className="px-2 py-2 font-semibold text-center">Tdk Masuk</th>
                  {indicators.map((i) => <th key={i.key} className="px-2 py-2 font-semibold text-center whitespace-nowrap">{shortLbl(i)}</th>)}
                </tr>
              </thead>
              <tbody>
                {Object.entries(monthlyForm.rows).map(([empId, r]) => {
                  const emp = empMap[empId] || {};
                  return (
                    <tr key={empId} className="border-t border-border">
                      <td className="px-3 py-2 font-semibold sticky left-0 bg-card">
                        <div>{emp.name || empId}</div><div className="text-[10px] text-muted-foreground">{lembagaMap[emp.lembaga_id] || "-"}</div>
                      </td>
                      <td className="px-1 py-1"><input type="number" className="w-14 text-center border border-border rounded px-1 py-1 bg-background" value={r.terlambat_count} onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, terlambat_count: e.target.value } } })} data-testid={`rapor-monthly-${empId}-terlambat`} /></td>
                      <td className="px-1 py-1"><input type="number" className="w-14 text-center border border-border rounded px-1 py-1 bg-background" value={r.tidak_masuk_count} onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, tidak_masuk_count: e.target.value } } })} data-testid={`rapor-monthly-${empId}-tdkmasuk`} /></td>
                      {indicators.map((ind) => (
                        <td key={ind.key} className="px-1 py-1">
                          <input type="number" step="0.5" min={0} max={SCALE_MAX} className="w-14 text-center border border-border rounded px-1 py-1 bg-background" value={r.scores?.[ind.key] ?? 0}
                            onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, scores: { ...r.scores, [ind.key]: Number(e.target.value) } } } })}
                            data-testid={`rapor-monthly-${empId}-${ind.key}`} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {!Object.keys(monthlyForm.rows).length && <tr><td colSpan={3 + indicators.length} className="px-5 py-10 text-center text-muted-foreground">Tidak ada karyawan</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isKaryawan && tab === "rekap" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-3 border-b border-border flex flex-wrap gap-3 items-end">
            <div><Label className="text-xs">Periode</Label><Input type="month" value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} className="w-40" data-testid="rapor-rekap-period" /></div>
            <div><Label className="text-xs">Lembaga</Label>
              <Select value={filterLembaga} onValueChange={setFilterLembaga}>
                <SelectTrigger className="w-56" data-testid="rapor-rekap-lembaga"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Seluruh Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={exportRekapExcel} data-testid="rapor-rekap-excel"><Download size={14} className="mr-1.5" />Excel</Button>
              <Button size="sm" onClick={printRekap} data-testid="rapor-rekap-pdf"><Printer size={14} className="mr-1.5" />Cetak PDF</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Guru</th><th className="px-3 py-2">Jabatan</th><th className="px-3 py-2">Lembaga</th>
                  <th className="px-3 py-2 text-center">Trlbt</th><th className="px-3 py-2 text-center">Tdk Msk</th>
                  {indicators.map((i) => <th key={i.key} className="px-2 py-2 text-center whitespace-nowrap">{shortLbl(i)}</th>)}
                  <th className="px-3 py-2 text-center">Rata²</th><th className="px-3 py-2 text-center">Grade</th>
                </tr>
              </thead>
              <tbody>
                {rekap?.rows?.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 font-semibold">{r.name || r.employee_id}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.position || "-"}</td>
                    <td className="px-3 py-2">{r.lembaga_name || "-"}</td>
                    <td className="px-3 py-2 text-center font-mono">{r.terlambat_count}</td>
                    <td className="px-3 py-2 text-center font-mono">{r.tidak_masuk_count}</td>
                    {indicators.map((ind) => <td key={ind.key} className="px-2 py-2 text-center font-mono">{r.scores?.[ind.key] ?? "-"}</td>)}
                    <td className="px-3 py-2 text-center font-mono font-bold">{r.average}</td>
                    <td className="px-3 py-2 text-center"><span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${r.grade === "A" ? "bg-emerald-100 text-emerald-700" : r.grade === "B" ? "bg-blue-100 text-blue-700" : r.grade === "C" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{r.grade}</span></td>
                  </tr>
                ))}
                {!rekap?.rows?.length && <tr><td colSpan={5 + indicators.length + 2} className="px-5 py-10 text-center text-muted-foreground">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rapor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Rapor" : "Buat Rapor Bulanan"}</DialogTitle>
            <DialogDescription>Skala penilaian 0–5. Nama & jabatan otomatis dari database karyawan.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Guru/Karyawan *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="rapor-employee-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.name} — {e.position || "-"}</SelectItem>)}</SelectContent>
              </Select>
              {form.employee_id && empMap[form.employee_id] && (
                <div className="mt-1.5 text-xs text-muted-foreground">
                  Jabatan: <span className="font-semibold text-foreground">{empMap[form.employee_id].position || "-"}</span> · Lembaga: <span className="font-semibold text-foreground">{lembagaMap[empMap[form.employee_id].lembaga_id] || "-"}</span>
                </div>
              )}
            </div>
            <div><Label>Penilaian Bulan *</Label><Input type="month" value={form.period || ""} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="rapor-period-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Terlambat</Label><Input type="number" min={0} value={form.terlambat_count || 0} onChange={(e) => setForm({ ...form, terlambat_count: e.target.value })} data-testid="rapor-terlambat" /></div>
              <div><Label className="text-xs">Tidak Masuk (Izin/Sakit)</Label><Input type="number" min={0} value={form.tidak_masuk_count || 0} onChange={(e) => setForm({ ...form, tidak_masuk_count: e.target.value })} data-testid="rapor-tdkmasuk" /></div>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-xs uppercase tracking-widest font-bold text-[#0B8D95] mb-2">Indikator Penilaian (0–5)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {indicators.map((ind) => (
                <div key={ind.key} className="flex items-center gap-3 p-2 border border-border rounded-lg">
                  <div className="flex-1 text-sm">{ind.label}</div>
                  <input type="range" min={0} max={SCALE_MAX} step={0.5} value={form.scores?.[ind.key] || 0} onChange={(e) => setForm({ ...form, scores: { ...form.scores, [ind.key]: Number(e.target.value) } })} className="w-24" data-testid={`rapor-score-${ind.key}`} />
                  <div className="w-10 text-right font-mono font-bold text-[#0B8D95]">{form.scores?.[ind.key] ?? 0}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3"><Label>Catatan</Label><Textarea value={form.catatan || ""} onChange={(e) => setForm({ ...form, catatan: e.target.value })} rows={2} /></div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="rapor-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
